import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { Auth, sendPasswordResetEmail, signOut } from '@angular/fire/auth';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, from, map, of, switchMap } from 'rxjs';

import { TenancyService } from '../../core/tenancy/tenancy.service';
import { PendingRequestsService } from '../../core/notifications/pending-requests.service';
import { OrdersStore } from '../../core/stores/orders.store';
import { PermissionService } from '../../core/permissions/permission.service';
import { EmployeeService } from '../../shared/services/employee.service';
import { environment } from '../../../environments/environment';
import { SHIFT_LABEL, shiftAt } from '../shift-footer/shift-window';
import { PageSubtitleService } from './page-subtitle.service';
import { NvButton } from '../../shared/ui/button/nv-button';
import { NvIcon } from '../../shared/ui/icon/nv-icon';
import { NvMenu, NvMenuItem, NvMenuTrigger } from '../../shared/ui/menu/nv-menu';
import { NvToastService } from '../../shared/ui/toast/nv-toast';
import { HeaderSearchResults } from './header-search-results/header-search-results';
import { HeaderNotificationsPanel } from './header-notifications-panel/header-notifications-panel';

/**
 * The compact header — "Workspace Mock/NexVenueX Console.dc.html" §3: rail
 * toggle, title + crumb, search, Ask AI, notifications bell, profile chip.
 *
 * Concern 14 — search and the bell were both real gaps, not deliberate
 * placeholders as this comment used to claim: search was a permanently
 * `aria-hidden`, `cursor: not-allowed` `<div>` with no store to search
 * because the stores it needed didn't exist yet when this header was first
 * built; the bell had a live badge (PendingRequestsService.count) but no
 * click handler at all, which reads as worse than disabled — it looks
 * live and does nothing. Both are wired now:
 *  - Search opens `HeaderSearchResults` (lazily mounted, see its own doc
 *    comment) in a `cdkConnectedOverlay`, filtering client-side over
 *    already-shared stores — never issuing a query of its own.
 *  - The bell opens `HeaderNotificationsPanel` (same lazy-mount reasoning)
 *    the same way, condensing guest requests + orders awaiting approval.
 * The language toggle that used to sit between them is gone outright —
 * there is no staff-console i18n infrastructure (GUEST_STRINGS covers the
 * guest surface only), and two permanently disabled buttons were worse
 * than no control.
 */
@Component({
  selector: 'nv-header',
  imports: [
    OverlayModule,
    RouterLink,
    NvButton,
    NvIcon,
    NvMenu,
    NvMenuItem,
    NvMenuTrigger,
    HeaderSearchResults,
    HeaderNotificationsPanel,
  ],
  templateUrl: './nv-header.html',
  styleUrl: './nv-header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NvHeader {
  private readonly router = inject(Router);
  private readonly auth = inject(Auth);
  private readonly toasts = inject(NvToastService);
  protected readonly tenancy = inject(TenancyService);
  private readonly requests = inject(PendingRequestsService);
  private readonly ordersStore = inject(OrdersStore);
  private readonly permissions = inject(PermissionService);
  private readonly employees = inject(EmployeeService);
  private readonly subtitles = inject(PageSubtitleService);

  readonly pageTitle = input('');
  /** Fallback subtitle (the venue name) for a route PageSubtitleService has no line for. */
  readonly pageCrumb = input('');

  readonly toggleRail = output<void>();

  /** Concern 8 — used to count guest requests only, so a placed food order gave no signal here at all. OrdersStore is already running app-wide (the requests-tasks-dock keeps it alive), so this adds no new listener. Stage 15 adds unverified guest UPI payment claims — the bell is the "needs attention" surface, unlike the rail's Tables badge which stays floor-only. */
  protected readonly notificationCount = computed(
    () =>
      this.requests.count() +
      this.ordersStore.pendingApproval().length +
      this.requests.orderedPaymentClaims().length +
      this.requests.orderedDayCloseAlerts().length +
      this.requests.orderedOpsAlerts().length
  );
  protected readonly canUseAi = computed(() => this.permissions.can('use_venue_ai'));

  /** Per-route line under the title — "N of M tables in use", "Your café at a glance", … */
  protected readonly subtitle = computed(() => this.subtitles.subtitle() || this.pageCrumb());

  private readonly now = signal(Date.now());
  /** Same clock-based shift windows the footer uses (shift-footer/shift-window.ts). */
  protected readonly shiftLabel = computed(() => `${SHIFT_LABEL[shiftAt(this.now())]} shift open`);

  /** "New order" only for someone who can actually take one — same gate as the Orders rail link. */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  /** Hidden on /orders, which has its own "New order" (the staff order dialog) — two same-named buttons on one page is confusing. */
  protected readonly canStartOrder = computed(
    () =>
      environment.previewFeatures &&
      this.permissions.can('manage_orders') &&
      !this.url().startsWith('/orders')
  );

  protected readonly currentEmail = computed(() => this.auth.currentUser?.email ?? null);

  /**
   * The signed-in user's own employee name — auth claims carry no display name.
   * One read per venue switch (EmployeeService.findSelf), not the TeamStore
   * roster: the header is on every screen, and TeamStore opens employee,
   * attendance and role listeners for everyone.
   */
  private readonly selfName = toSignal(
    toObservable(this.tenancy.licenseId).pipe(
      switchMap((licenseId) => {
        const user = this.auth.currentUser;
        if (!licenseId || !user) return of(null);
        return from(this.employees.findSelf(user.uid, user.email)).pipe(
          map((employee) => employee?.name?.trim() || null),
          catchError(() => of(null))
        );
      })
    ),
    { initialValue: null }
  );

  protected readonly displayName = computed(() => {
    const email = this.currentEmail();
    if (!email) return 'Account';
    return this.selfName() || email.split('@')[0];
  });

  protected readonly roleLabel = computed(() => {
    switch (this.tenancy.role()) {
      case 'root':
        return 'Platform admin';
      case 'admin':
        return 'Owner';
      case 'manager':
        return 'Manager';
      case null:
        return 'Signed out';
      default:
        return 'Staff';
    }
  });

  protected readonly initials = computed(() => this.displayName().charAt(0).toUpperCase() || '?');

  protected readonly sendingReset = signal(false);

  protected readonly searchOpen = signal(false);

  protected readonly searchQuery = signal('');
  protected readonly notifOpen = signal(false);

  constructor() {
    const tick = setInterval(() => this.now.set(Date.now()), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(tick));
  }

  protected openSearch(): void {
    this.notifOpen.set(false);
    this.searchOpen.set(true);
  }

  protected closeSearch(): void {
    this.searchOpen.set(false);
  }

  protected clearSearch(): void {
    this.searchQuery.set('');
  }

  protected toggleNotifications(): void {
    this.searchOpen.set(false);
    this.notifOpen.update((open) => !open);
  }

  protected closeNotifications(): void {
    this.notifOpen.set(false);
  }

  protected newOrder(): void {
    void this.router.navigate(['/tables'], { queryParams: { filter: 'free' } });
    this.toasts.open('Pick a free table to start a new order.', 'Dismiss', { duration: 4000 });
  }

  /** Ported verbatim from App.resetOwnPassword — same call, same copy, only moved into the header's account menu. */
  protected async resetOwnPassword(): Promise<void> {
    const email = this.currentEmail();
    if (!email) return;
    this.sendingReset.set(true);
    try {
      await sendPasswordResetEmail(this.auth, email);
      this.toasts.open(`Password reset link sent to ${email}.`, 'Dismiss', { duration: 6000 });
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not send the reset link.', 'Dismiss', {
        duration: 5000,
      });
    } finally {
      this.sendingReset.set(false);
    }
  }

  protected async signOut(): Promise<void> {
    await signOut(this.auth);
    void this.router.navigate(['/auth/login']);
  }
}
